import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { useSignedUrl } from "@/hooks/useSignedUrl";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Trash2, Plus, Edit, FolderOpen } from "lucide-react";

interface Course {
  id: string;
  title: string;
}

interface Media {
  id: string;
  title: string;
  description: string | null;
  media_type: string;
  image_url: string;
  video_url: string | null;
  display_order: number;
  course_id: string | null;
  courses?: {
    title: string;
  };
}

// Component to display a single media item with signed URL
const MediaImage = ({ imageUrl, alt, className }: { imageUrl: string; alt: string; className: string }) => {
  const { signedUrl, loading } = useSignedUrl("mission-photos", imageUrl);
  
  if (loading) {
    return <div className={className + " animate-pulse bg-muted"} />;
  }
  
  return <img src={signedUrl} alt={alt} className={className} />;
};

export const BibleSchoolGallery = () => {
  const [media, setMedia] = useState<Media[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMedia, setEditingMedia] = useState<Media | null>(null);
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    media_type: "photo",
    image_url: "",
    video_url: "",
    course_id: "",
    display_order: 0,
  });

  useEffect(() => {
    fetchMedia();
    fetchCourses();
  }, []);

  const fetchMedia = async () => {
    const { data, error } = await supabase
      .from("bible_school_media")
      .select(`
        *,
        courses (
          title
        )
      `)
      .order("display_order", { ascending: true });

    if (error) {
      toast.error("Failed to load media");
      return;
    }

    setMedia(data || []);
  };

  const fetchCourses = async () => {
    const { data, error } = await supabase
      .from("courses")
      .select("id, title")
      .eq("published", true)
      .order("title");

    if (error) {
      toast.error("Failed to load courses");
      return;
    }

    setCourses(data || []);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUploading(true);
    setUploadProgress(0);
    setCurrentFileIndex(0);

    try {
      // Handle editing existing gallery (all items with same title)
      if (editingMedia) {
        // Update all items in the gallery
        const updateData = {
          title: formData.title,
          description: formData.description || null,
          course_id: formData.course_id === "none" ? null : formData.course_id || null,
          display_order: formData.display_order,
        };

        const { error } = await supabase
          .from("bible_school_media")
          .update(updateData)
          .eq("title", editingMedia.title);

        if (error) {
          toast.error("Failed to update gallery");
          setIsUploading(false);
          return;
        }

        // Upload new files if any were selected
        if (uploadingFiles.length > 0) {
          const mediaItems = [];
          const existingCount = media.filter(m => m.title === formData.title).length;
          
          for (let i = 0; i < uploadingFiles.length; i++) {
            setCurrentFileIndex(i + 1);
            const file = uploadingFiles[i];
            const fileExt = file.name.split('.').pop()?.toLowerCase();
            const fileName = `bible-school/${Date.now()}-${i}.${fileExt}`;
            
            setUploadProgress(((i) / uploadingFiles.length) * 100);
            
            const { error: uploadError } = await supabase.storage
              .from('mission-photos')
              .upload(fileName, file);

            if (uploadError) throw uploadError;
            
            setUploadProgress(((i + 1) / uploadingFiles.length) * 100);

            mediaItems.push({
              title: formData.title,
              description: formData.description || null,
              media_type: editingMedia.media_type,
              image_url: fileName,
              video_url: editingMedia.video_url || null,
              course_id: formData.course_id === "none" ? null : formData.course_id || null,
              display_order: formData.display_order + existingCount + i,
            });
          }

          const { error: insertError } = await supabase
            .from("bible_school_media")
            .insert(mediaItems);

          if (insertError) {
            toast.error("Failed to add new images");
            setIsUploading(false);
            return;
          }

          toast.success(`Gallery updated and ${mediaItems.length} image(s) added`);
        } else {
          toast.success("Gallery updated successfully");
        }
      } else {
        // Handle adding new media with multiple files
        if (uploadingFiles.length === 0 && !formData.image_url) {
          toast.error("Please upload files or provide an image URL");
          setIsUploading(false);
          return;
        }

        const mediaItems = [];

        // Upload all selected files
        if (uploadingFiles.length > 0) {
          for (let i = 0; i < uploadingFiles.length; i++) {
            setCurrentFileIndex(i + 1);
            const file = uploadingFiles[i];
            const fileExt = file.name.split('.').pop()?.toLowerCase();
            const fileName = `bible-school/${Date.now()}-${i}.${fileExt}`;
            
            // Show progress before upload
            setUploadProgress(((i) / uploadingFiles.length) * 100);
            
            const { error: uploadError } = await supabase.storage
              .from('mission-photos')
              .upload(fileName, file);

            if (uploadError) throw uploadError;
            
            // Update progress after upload completes
            setUploadProgress(((i + 1) / uploadingFiles.length) * 100);

            // Store the storage path instead of public URL
            const storagePath = fileName;

            mediaItems.push({
              title: formData.title,
              description: formData.description || null,
              media_type: formData.media_type,
              image_url: storagePath, // Store path, will be converted to signed URL on read
              video_url: formData.video_url || null,
              course_id: formData.course_id === "none" ? null : formData.course_id || null,
              display_order: formData.display_order + i,
            });
          }
        } else if (formData.image_url) {
          // Single item from URL
          mediaItems.push({
            title: formData.title,
            description: formData.description || null,
            media_type: formData.media_type,
            image_url: formData.image_url,
            video_url: formData.video_url || null,
            course_id: formData.course_id === "none" ? null : formData.course_id || null,
            display_order: formData.display_order,
          });
        }

        const { error } = await supabase
          .from("bible_school_media")
          .insert(mediaItems);

        if (error) {
          toast.error("Failed to add media");
          setIsUploading(false);
          return;
        }

        toast.success(`${mediaItems.length} media item(s) added successfully`);
      }

      setIsDialogOpen(false);
      resetForm();
      fetchMedia();
    } catch (error: any) {
      toast.error(error.message || "Failed to save media");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteGallery = async (galleryTitle: string) => {
    const galleryMedia = media.filter(item => item.title === galleryTitle);
    const count = galleryMedia.length;
    
    if (!confirm(`Are you sure you want to delete all ${count} item(s) in "${galleryTitle}" gallery?`)) return;

    try {
      const ids = galleryMedia.map(item => item.id);
      const { error } = await supabase
        .from("bible_school_media")
        .delete()
        .in("id", ids);

      if (error) {
        toast.error("Failed to delete gallery");
        return;
      }

      toast.success(`Deleted ${count} item(s) from "${galleryTitle}"`);
      fetchMedia();
    } catch (error) {
      toast.error("Failed to delete gallery");
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this media?")) return;

    const { error } = await supabase
      .from("bible_school_media")
      .delete()
      .eq("id", id);

    if (error) {
      toast.error("Failed to delete media");
      return;
    }

    toast.success("Media deleted successfully");
    fetchMedia();
  };

  const handleEditGallery = (galleryTitle: string) => {
    const galleryItems = media.filter(item => item.title === galleryTitle);
    if (galleryItems.length === 0) return;
    
    // Use the first item to get gallery info
    const firstItem = galleryItems[0];
    setEditingMedia(firstItem); // Store first item to track we're editing
    setFormData({
      title: galleryTitle,
      description: firstItem.description || "",
      media_type: firstItem.media_type,
      image_url: "",
      video_url: "",
      course_id: firstItem.course_id || "none",
      display_order: firstItem.display_order,
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      media_type: "photo",
      image_url: "",
      video_url: "",
      course_id: "",
      display_order: 0,
    });
    setEditingMedia(null);
    setUploadingFiles([]);
    setUploadProgress(0);
    setCurrentFileIndex(0);
  };

  // Group media by title
  const galleries: Record<string, Media[]> = {};
  media.forEach(item => {
    if (!galleries[item.title]) {
      galleries[item.title] = [];
    }
    galleries[item.title].push(item);
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Bible School Gallery</h2>
        <Dialog
          open={isDialogOpen}
          onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}
        >
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add Media
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingMedia ? "Edit Gallery" : "Add New Media"}
              </DialogTitle>
              <DialogDescription>
                {editingMedia ? "Update the gallery details and course association." : "Add a new photo or video to the Bible School gallery."}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Gallery Title</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                />
              </div>

              {!editingMedia && (
                <div>
                  <Label htmlFor="media_type">Media Type</Label>
                  <Select
                    value={formData.media_type}
                    onValueChange={(value) =>
                      setFormData({ ...formData, media_type: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="photo">Photo</SelectItem>
                      <SelectItem value="video">Video</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label htmlFor="file_upload">
                  {editingMedia ? "Add More Images to Gallery" : `Upload ${formData.media_type === "video" ? "Thumbnail" : "Images (Multiple)"}`}
                </Label>
                <Input
                  id="file_upload"
                  type="file"
                  accept={formData.media_type === "video" ? "image/*" : "image/*,video/*"}
                  multiple
                  onChange={(e) => {
                    const files = e.target.files;
                    if (files) {
                      setUploadingFiles(Array.from(files));
                    }
                  }}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {uploadingFiles.length > 0 
                    ? `Selected: ${uploadingFiles.length} file(s) - ${uploadingFiles.map(f => f.name).join(", ")}` 
                    : editingMedia ? "Optional: Upload additional images for this gallery" : "Select multiple files or use URL below"}
                </p>
              </div>

              {!editingMedia && (
                <>
                  <div>
                    <Label htmlFor="image_url">
                      Image URL (Optional) {formData.media_type === "video" && "(Thumbnail)"}
                    </Label>
                    <Input
                      id="image_url"
                      value={formData.image_url}
                      onChange={(e) =>
                        setFormData({ ...formData, image_url: e.target.value })
                      }
                      placeholder="https://example.com/image.jpg"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Leave empty to use uploaded file
                    </p>
                  </div>

                  {formData.media_type === "video" && (
                    <div>
                      <Label htmlFor="video_url">Video URL (YouTube)</Label>
                      <Input
                        id="video_url"
                        value={formData.video_url}
                        onChange={(e) =>
                          setFormData({ ...formData, video_url: e.target.value })
                        }
                        placeholder="https://www.youtube.com/watch?v=..."
                      />
                    </div>
                  )}
                </>
              )}

              <div>
                <Label htmlFor="course_id">Link to Course (Optional)</Label>
                <Select
                  value={formData.course_id}
                  onValueChange={(value) =>
                    setFormData({ ...formData, course_id: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a course" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {courses.map((course) => (
                      <SelectItem key={course.id} value={course.id}>
                        {course.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="display_order">Display Order</Label>
                <Input
                  id="display_order"
                  type="number"
                  value={formData.display_order}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      display_order: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </div>

              {/* Upload Progress */}
              {isUploading && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      Uploading {currentFileIndex} of {uploadingFiles.length} file(s)...
                    </span>
                    <span className="font-medium">{Math.round(uploadProgress)}%</span>
                  </div>
                  <Progress value={uploadProgress} className="h-2" />
                </div>
              )}

              <div className="flex gap-2">
                <Button type="submit" className="flex-1" disabled={isUploading}>
                  {isUploading ? "Uploading..." : editingMedia ? "Update" : "Add"} Media
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsDialogOpen(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="galleries" className="space-y-4">
        <TabsList>
          <TabsTrigger value="galleries">
            <FolderOpen className="mr-2 h-4 w-4" />
            Galleries
          </TabsTrigger>
          <TabsTrigger value="all-media">All Media</TabsTrigger>
        </TabsList>

        <TabsContent value="galleries" className="space-y-6">
          {Object.entries(galleries).map(([galleryTitle, galleryMedia]) => (
            <Card key={galleryTitle}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                <div>
                  <CardTitle className="text-xl">{galleryTitle}</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    {galleryMedia.length} item(s)
                    {galleryMedia[0].courses && ` • ${galleryMedia[0].courses.title}`}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditGallery(galleryTitle)}
                  >
                    <Edit className="mr-2 h-4 w-4" />
                    Edit Gallery
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDeleteGallery(galleryTitle)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete Gallery
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {galleryMedia.map((item) => (
                    <div key={item.id} className="relative">
                      <MediaImage
                        imageUrl={item.image_url}
                        alt={item.title}
                        className="w-full h-48 object-cover rounded"
                      />
                      {item.media_type === "video" && (
                        <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                          Video
                        </div>
                      )}
                      <Button
                        variant="destructive"
                        size="sm"
                        className="absolute bottom-2 right-2"
                        onClick={() => handleDelete(item.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
          {Object.keys(galleries).length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              No galleries yet. Click "Add Media" to get started.
            </div>
          )}
        </TabsContent>

        <TabsContent value="all-media">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {media.map((item) => (
              <Card key={item.id}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">{item.title}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <MediaImage
                    imageUrl={item.image_url}
                    alt={item.title}
                    className="w-full h-48 object-cover rounded"
                  />
                  {item.description && (
                    <p className="text-sm text-muted-foreground">
                      {item.description}
                    </p>
                  )}
                  <div className="text-sm space-y-1">
                    <p>
                      <span className="font-medium">Type:</span>{" "}
                      {item.media_type === "video" ? "Video" : "Photo"}
                    </p>
                    {item.courses && (
                      <p>
                        <span className="font-medium">Course:</span>{" "}
                        {item.courses.title}
                      </p>
                    )}
                    <p>
                      <span className="font-medium">Order:</span>{" "}
                      {item.display_order}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="destructive"
                      size="sm"
                      className="w-full"
                      onClick={() => handleDelete(item.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {media.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              No media items yet. Click "Add Media" to get started.
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
